import { useCallback, useRef, useState } from 'react';
import {
  enviarAuditoria,
  ErroAuditoria,
  type DependenciasAuditoria,
  type RespostaAuditoriaPublica,
} from '../lib/auditoria';

export type EstadoAuditoria =
  | { fase: 'ocioso' }
  | { fase: 'enviando' }
  | { fase: 'sucesso'; resultado: RespostaAuditoriaPublica }
  | {
      fase: 'erro';
      codigo: string;
      mensagem: string;
      retryAfterSegundos?: number | undefined;
    };

/**
 * Estado da auditoria pública: ocioso → enviando → sucesso | erro.
 *
 * `dep` é injetável (mesmo padrão de `criarCliente` em `apps/web/src/lib/api.ts`)
 * só para o teste trocar `buscar` por um dublê, sem mexer em rede.
 *
 * Um `enviar` em voo é ignorado enquanto outro está em andamento — clique
 * duplo no botão, ou um segundo arquivo solto durante o envio do primeiro,
 * não deve abrir uma segunda requisição.
 */
export function useAuditoria(dep: DependenciasAuditoria = { buscar: fetch }): {
  estado: EstadoAuditoria;
  enviar: (arquivo: File) => Promise<void>;
  reiniciar: () => void;
} {
  const [estado, setEstado] = useState<EstadoAuditoria>({ fase: 'ocioso' });
  const emVoo = useRef(false);

  const enviar = useCallback(
    async (arquivo: File) => {
      if (emVoo.current) return;
      emVoo.current = true;
      setEstado({ fase: 'enviando' });
      try {
        const resultado = await enviarAuditoria(arquivo, dep);
        setEstado({ fase: 'sucesso', resultado });
      } catch (erro) {
        if (erro instanceof ErroAuditoria) {
          setEstado({
            fase: 'erro',
            codigo: erro.codigo,
            mensagem: erro.message,
            retryAfterSegundos: erro.retryAfterSegundos,
          });
        } else {
          setEstado({
            fase: 'erro',
            codigo: 'rede',
            mensagem: 'Não foi possível conectar. Confira sua internet e tente de novo.',
          });
        }
      } finally {
        emVoo.current = false;
      }
    },
    [dep],
  );

  const reiniciar = useCallback(() => setEstado({ fase: 'ocioso' }), []);

  return { estado, enviar, reiniciar };
}
